import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Plus, Prohibit, MagnifyingGlass, PaperPlaneTilt, QrCode, ArrowLeft } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Pager } from "@/components/Pager";
import Logo from "@/components/Logo";
import OneKiteQR from "@/components/OneKiteQR";

const CHIP = {
  UNASSIGNED: "bg-white/20 text-white",
  DISTRIBUTED: "bg-white/25 text-white",
  ACTIVE: "bg-white/25 text-white",
  REVOKED: "bg-red-500/40 text-white",
};
const FACE = {
  UNASSIGNED: "from-slate-500 to-slate-700",
  DISTRIBUTED: "from-blue-500 to-blue-700",
  ACTIVE: "from-primary to-emerald-700",
  REVOKED: "from-zinc-400 to-zinc-600",
};

function CardTile({ c, onOpen, onQr }) {
  return (
    <div data-testid={`card-tile-${c.card_reference}`} onClick={() => onOpen(c)}
      className={`relative rounded-2xl p-4 aspect-[1.6] flex flex-col justify-between text-white cursor-pointer bg-gradient-to-br ${FACE[c.status] || FACE.UNASSIGNED} shadow-sm hover:-translate-y-0.5 transition-transform overflow-hidden`}>
      <div className="flex items-start justify-between">
        <Logo mark variant="white" className="h-6" />
        <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 ${CHIP[c.status]}`}>{c.status}</span>
      </div>
      <div className="flex items-end justify-between">
        <div className="min-w-0">
          <p className="font-mono font-semibold tracking-wider text-sm">{c.card_reference}</p>
          <p className="text-[10px] opacity-80 truncate">{c.distributed_to?.business_name || "OneKite network"}</p>
        </div>
        {c.status !== "REVOKED" && <button data-testid={`view-qr-${c.card_reference}`} onClick={(e) => { e.stopPropagation(); onQr(c); }} className="shrink-0 opacity-80 hover:opacity-100 p-1"><QrCode size={20} /></button>}
      </div>
    </div>
  );
}

export default function AdminKiteCards() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [expBatch, setExpBatch] = useState(null);
  const [expCfg, setExpCfg] = useState({ mode: "individual", scope: "all", paper: "A4", bleed_mm: 3, mark_printed: false });
  const [businesses, setBusinesses] = useState([]);
  const [count, setCount] = useState(50);
  const [note, setNote] = useState("");
  // inventory
  const [cards, setCards] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ASSIGNED");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE = 12;
  // selected batch view
  const [selBatch, setSelBatch] = useState(null);
  const [batchCards, setBatchCards] = useState([]);
  // dialogs
  const [distBatch, setDistBatch] = useState(null);
  const [distBiz, setDistBiz] = useState("");
  const [detail, setDetail] = useState(null);
  const [qrCard, setQrCard] = useState(null);

  const loadStats = () => api.get("/admin/cards/stats").then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const loadBatches = () => api.get("/admin/cards/batches").then((r) => setBatches(r.data)).catch(() => {});
  const runExport = async () => {
    try {
      const res = await api.post(`/admin/cards/batch/${expBatch.id}/export-pdf`, expCfg, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `${expBatch.batch_reference}-${expCfg.mode}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Print-ready PDF generated"); setExpBatch(null); loadBatches();
    } catch (e) {
      let msg = "Export failed";
      try { const j = JSON.parse(await e.response.data.text()); const d = j.detail;
        msg = (d?.message || d || msg) + (d?.problems ? ": " + d.problems.map((p) => `${p.ref} (${p.issue})`).join(", ") : "");
      } catch { /* keep default */ }
      toast.error(msg);
    }
  };
  const loadInventory = () => {
    const params = { limit: PAGE, offset: page * PAGE };
    if (statusFilter === "ASSIGNED") params.assigned = true;
    else params.status = statusFilter;
    if (query) params.q = query;
    api.get("/admin/cards", { params }).then((r) => { setCards(r.data.items); setTotal(r.data.total); }).catch((e) => toast.error(apiError(e.response?.data?.detail) || "Couldn't load cards"));
  };
  const loadBatchCards = (batchId) => api.get("/admin/cards", { params: { batch_id: batchId, limit: 200 } }).then((r) => setBatchCards(r.data.items)).catch(() => setBatchCards([]));

  useEffect(() => { loadStats(); loadBatches(); api.get("/admin/businesses").then((r) => setBusinesses(r.data.items || r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "inventory") loadInventory(); }, [tab, statusFilter, query, page]); // eslint-disable-line

  const generate = async () => {
    try {
      const { data } = await api.post("/admin/cards/batch", { count: Number(count), note });
      toast.success(`Generated ${data.created} cards (${data.batch.batch_reference})`);
      setNote(""); loadStats(); loadBatches();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const distribute = async () => {
    if (!distBiz) return toast.error("Choose a business");
    try {
      const { data } = await api.post("/admin/cards/distribute", { batch_id: distBatch.id, business_id: distBiz });
      toast.success(`Distributed ${data.distributed} cards`);
      setDistBatch(null); setDistBiz(""); loadStats();
      const r = await api.get("/admin/cards/batches"); setBatches(r.data);
      if (selBatch) {
        const fresh = r.data.find((b) => b.id === selBatch.id);
        if (fresh) setSelBatch(fresh);
        loadBatchCards(selBatch.id);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async (card) => {
    if (!window.confirm(`Revoke card ${card.card_reference}? It will stop working immediately.`)) return;
    try {
      await api.post(`/admin/cards/${card.id}/revoke`); toast.success("Card revoked");
      loadStats(); if (tab === "inventory") loadInventory(); if (selBatch) loadBatchCards(selBatch.id);
      setDetail(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openDetail = async (card) => {
    try { const { data } = await api.get(`/admin/cards/${card.id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openBatch = (b) => { setSelBatch(b); loadBatchCards(b.id); };

  return (
    <div className="space-y-6" data-testid="admin-cards-page">
      <div>
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><CreditCard size={26} className="text-primary" /> Kite Cards</h1>
        <p className="text-muted-foreground text-sm">Network-owned OneKite credentials. Pre-generate, distribute, and manage card inventory.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setSelBatch(null); setTab(v); }}>
        <TabsList className="bg-muted/50 rounded-full">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Overview</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Batches</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Inventory</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6 mt-5">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cards-stats">
              {[["Unassigned", stats.unassigned], ["Distributed", stats.distributed], ["Active", stats.active], ["Revoked", stats.revoked]].map(([l, v]) => (
                <div key={l} className="card-surface p-4">
                  <p className="font-display font-bold text-2xl" data-testid={`stat-${l.toLowerCase()}`}>{v}</p>
                  <p className="text-xs text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          )}
          <div className="card-surface p-5">
            <p className="font-display font-semibold mb-3 flex items-center gap-2"><Plus size={18} className="text-primary" /> Generate card batch</p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Number of cards</Label>
                <Input data-testid="batch-count" type="number" value={count} onChange={(e) => setCount(e.target.value)} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl w-32" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-muted-foreground">Note (optional)</Label>
                <Input data-testid="batch-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Petrol boda cards" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <Button data-testid="generate-batch" onClick={generate} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Generate</Button>
            </div>
          </div>
        </TabsContent>

        {/* BATCHES */}
        <TabsContent value="batches" className="mt-5">
          {!selBatch ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="batches-grid">
              {batches.length === 0 && <p className="text-sm text-muted-foreground">No batches yet — generate one in Overview.</p>}
              {batches.map((b) => (
                <div key={b.id} className="card-surface p-5 flex flex-col gap-3" data-testid={`batch-card-${b.id}`}>
                  <div>
                    <p className="font-display font-semibold text-lg">{b.batch_reference}</p>
                    <p className="text-xs text-muted-foreground">{b.count} cards{b.note ? ` · ${b.note}` : ""}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-slate-500">{b.unassigned} unassigned</span>
                    <span className="text-blue-600">{b.distributed} distributed</span>
                    <span className="text-primary">{b.active} active</span>
                    <span className="text-destructive">{b.revoked} revoked</span>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant="outline" data-testid={`view-batch-${b.id}`} onClick={() => openBatch(b)} className="rounded-lg h-9 border-black/10 flex-1">View cards</Button>
                    <Button size="sm" variant="outline" disabled={b.unassigned === 0} data-testid={`distribute-${b.id}`} onClick={() => setDistBatch(b)} className="rounded-lg h-9 border-black/10"><PaperPlaneTilt size={15} className="mr-1" /> Distribute</Button>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`export-${b.id}`} onClick={() => { setExpCfg({ mode: "individual", scope: "all", paper: "A4", bleed_mm: 3, mark_printed: false }); setExpBatch(b); }} className="rounded-lg h-9 border-primary/30 text-primary"><QrCode size={15} className="mr-1" /> Export print PDF</Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button onClick={() => setSelBatch(null)} data-testid="back-to-batches" className="text-muted-foreground flex items-center gap-1 text-sm hover:text-foreground"><ArrowLeft size={16} /> Batches</button>
                <div className="flex items-center gap-3">
                  <p className="font-display font-semibold">{selBatch.batch_reference} · {batchCards.length} cards</p>
                  <Button size="sm" variant="outline" disabled={selBatch.unassigned === 0} data-testid="distribute-selected" onClick={() => setDistBatch(selBatch)} className="rounded-lg h-9 border-black/10"><PaperPlaneTilt size={15} className="mr-1" /> Distribute</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="batch-cards-grid">
                {batchCards.map((c) => <CardTile key={c.id} c={c} onOpen={openDetail} onQr={setQrCard} />)}
              </div>
            </div>
          )}
        </TabsContent>

        {/* INVENTORY */}
        <TabsContent value="inventory" className="space-y-4 mt-5">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground mr-auto">Distributed & assigned cards and their status.</p>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger data-testid="status-filter" className="h-9 w-44 bg-muted/40 border-black/10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ASSIGNED">Distributed & beyond</SelectItem>
                <SelectItem value="DISTRIBUTED">Distributed</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="REVOKED">Revoked</SelectItem>
                <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="card-search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} placeholder="OK-…" className="h-9 pl-8 bg-muted/40 border-black/10 rounded-xl text-sm w-40" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cards-inventory">
            {cards.length === 0 && <p className="text-sm text-muted-foreground">No cards match.</p>}
            {cards.map((c) => <CardTile key={c.id} c={c} onOpen={openDetail} onQr={setQrCard} />)}
          </div>
          <Pager page={page} total={total} pageSize={PAGE} onPage={setPage} />
        </TabsContent>
      </Tabs>

      {/* Export print PDF dialog */}
      <Dialog open={!!expBatch} onOpenChange={(o) => !o && setExpBatch(null)}>
        <DialogContent data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Export print PDF — {expBatch?.batch_reference}</DialogTitle>
            <DialogDescription>Print-ready OneKite card (portrait 50 × 90 mm). Stamps the official vector artwork and overlays each card's existing QR credential — no new tokens are created.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Layout mode</Label>
              <Select value={expCfg.mode} onValueChange={(v) => setExpCfg({ ...expCfg, mode: v })}>
                <SelectTrigger data-testid="export-mode" className="bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="individual">Individual card pages (prepress)</SelectItem><SelectItem value="sheet">Print sheet (crop marks)</SelectItem></SelectContent>
              </Select></div>
            {expCfg.mode === "sheet" && <div><Label className="text-xs text-muted-foreground">Paper size</Label>
              <Select value={expCfg.paper} onValueChange={(v) => setExpCfg({ ...expCfg, paper: v })}>
                <SelectTrigger data-testid="export-paper" className="bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="A4">A4</SelectItem><SelectItem value="A3">A3</SelectItem><SelectItem value="Letter">Letter</SelectItem></SelectContent>
              </Select></div>}
            <div><Label className="text-xs text-muted-foreground">Bleed (mm)</Label>
              <Input data-testid="export-bleed" type="number" value={expCfg.bleed_mm} onChange={(e) => setExpCfg({ ...expCfg, bleed_mm: Number(e.target.value) })} className="bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="export-mark-printed" checked={expCfg.mark_printed} onChange={(e) => setExpCfg({ ...expCfg, mark_printed: e.target.checked })} className="accent-primary h-4 w-4" /> Mark unassigned cards as PRINTED (keeps them un-activated)</label>
            <Button data-testid="confirm-export" onClick={runExport} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Generate PDF</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Distribute dialog */}
      <Dialog open={!!distBatch} onOpenChange={(o) => !o && setDistBatch(null)}>
        <DialogContent data-testid="distribute-dialog">
          <DialogHeader>
            <DialogTitle>Distribute {distBatch?.batch_reference}</DialogTitle>
            <DialogDescription>Assign the {distBatch?.unassigned} unassigned cards in this batch to a business.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Business</Label>
            <Select value={distBiz} onValueChange={setDistBiz}>
              <SelectTrigger data-testid="distribute-business" className="bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue placeholder="Select business" /></SelectTrigger>
              <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="confirm-distribute" onClick={distribute} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Distribute cards</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR preview dialog */}
      <Dialog open={!!qrCard} onOpenChange={(o) => !o && setQrCard(null)}>
        <DialogContent data-testid="card-qr-dialog">
          <DialogHeader>
            <DialogTitle>{qrCard?.card_reference}</DialogTitle>
            <DialogDescription>Opaque credential token — resolves server-side. Print onto a physical card.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">{qrCard?.status === "REVOKED" ? <p className="text-sm text-muted-foreground py-10 text-center">This card has been revoked and can no longer be scanned.</p> : <OneKiteQR value={qrCard?.token} size={220} />}</div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="card-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.card.card_reference}</DialogTitle>
            <DialogDescription>Card status, activation and replacement history.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 text-white bg-gradient-to-br ${FACE[detail.card.status]}`}>{detail.card.status}</span>
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setQrCard(detail.card)} className="rounded-lg h-8 border-black/10"><QrCode size={15} className="mr-1" /> QR</Button>
                  {detail.card.status !== "REVOKED" && <Button size="sm" variant="outline" data-testid="detail-revoke" onClick={() => revoke(detail.card)} className="rounded-lg h-8 border-destructive/30 text-destructive"><Prohibit size={15} className="mr-1" /> Revoke</Button>}
                </div>
              </div>
              {detail.card.distributed_to && <p className="text-muted-foreground">Distributed to <span className="text-foreground">{detail.card.distributed_to.business_name}</span> · {fmtDateTime(detail.card.distributed_to.at)}</p>}
              {detail.customer && <p className="text-muted-foreground">Assigned to <span className="text-foreground">{detail.customer.name}</span> · OneKite ID <span className="font-mono">{detail.customer.onekite_id_formatted}</span></p>}
              {detail.card.activated_by && <p className="text-muted-foreground">Activated by {detail.card.activated_by.staff_name} · {fmtDateTime(detail.card.activated_at)}</p>}
              {detail.replacement_history.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Replaced cards</p>
                  {detail.replacement_history.map((h, i) => <p key={i} className="text-muted-foreground font-mono">{h.card_reference} · {h.status}</p>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
